import React, { FC, useEffect, useRef } from 'react'
import './hero-complex.module.scss'
import { APIENDPOINT, getCWPEnvironmentForURL, sanitizeHTML } from '../../../../utils/common-utils'
import { HeroComplexProps } from './HeroComplex.types'

function checkHeight(headingRef: React.RefObject<HTMLHeadingElement | null>) {
  if (headingRef.current!.clientHeight <= 53) {
    headingRef.current!.classList.add('Type-Display')
  }
  if (headingRef.current!.clientHeight > 102) {
    headingRef.current!.classList.remove('Type-Display')
  }
}

const heroPattern = (content: any) => {
  const { backgroundFacet, featuredImage, backgroundImage } = content
  if (backgroundFacet && featuredImage) {
    return 'facetCutout'
  } else if (backgroundImage && !backgroundFacet) {
    return 'imageOverlay'
  } else {
    return 'illustration'
  }
}

const addBorder = (content: any) => {
  const { backgroundFacet, backgroundImage } = content
  return !backgroundFacet && !backgroundImage ? 'ComplexHero-border' : ''
}

export const HeroComplex: FC<HeroComplexProps> = props => {
  const { content, testId } = props
  const {
    featuredImage,
    featuredImageCaption,
    backgroundFacet,
    backgroundImage,
    textContent,
    buttonTarget,
    buttonUrl,
    buttonText,
    analytics,
    heading,
    noClip,
    brandExtension,
    buttonLinkAltText,
    heroCompId,
  } = content
  const headingRef = useRef<HTMLHeadingElement>(null)

  const bottomPaddingOnMobileWithNoButton = buttonText
    ? ''
    : 'u-sm-paddingBottom32 u-md-paddingBottom32 '

  useEffect(() => {
    const handleResize = () => checkHeight(headingRef)
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const env = getCWPEnvironmentForURL(window.location.href)
  return (
    <>
      {backgroundImage && (
        <style
          type='text/css'
          dangerouslySetInnerHTML={{
            __html: `
           [id="${heroCompId}"]
           .ComplexHero-background { background: ${`url(${backgroundImage});`}
             background-size: cover;
            background-position: center center; 
          }; }
      `,
          }}
        />
      )}

      {backgroundFacet && (
        <style
          type='text/css'
          dangerouslySetInnerHTML={{
            __html: `
            [id="${heroCompId}"]
            .ComplexHero-background { background: url(${
              APIENDPOINT(env).baseGallery
            }content/gallery/ampmicrosites/facets/hero-facets-${
              backgroundFacet || 'midnight-blue'
            }.svg);
            background-size: cover;
            background-position: center center;
            }
      `,
          }}
        />
      )}

      <div className='ComplexHero-container u-posRelative'>
        <section
          className={`ComplexHero ComplexHero--${heroPattern(content)}${
            noClip ? '' : ' u-posRelative u-paddingLeft40 u-sm-paddingLeft0 u-md-paddingLeft0'
          } ${brandExtension}`}
          data-analytics-container='Hero'
        >
          <div
            data-testid={testId ? `${testId}-background-content` : null}
            className={`ComplexHero-background  u-posAbsolute ${addBorder(content)}${
              noClip ? 'ComplexHero--fullWidth u-sizeFull' : ''
            }`}
          />
          <div
            className={`u-flex u-sm-flexColReverse u-md-flexColReverse u-lg-paddingLeft16 u-posRelative ${addBorder(
              content
            )}`}
          >
            <div className='ComplexHero-mobileOverlay u-lg-size6of12'>
              <div
                className={`ComplexHero-content u-sm-marginHoriz20 u-md-marginHoriz20 u-paddingHoriz16 ${
                  heroPattern(content) === 'imageOverlay' ? 'u-marginLeft16' : ''
                } u-sm-bgColorWhite u-md-bgColorWhite 
                ${
                  backgroundImage ? 'u-bgColorWhite u-borderColorNeutralHero' : ''
                } ${bottomPaddingOnMobileWithNoButton}`}
                data-testid={testId ? `${testId}-content` : null}
              >
                <h1
                  ref={headingRef}
                  className={'u-marginTop32 u-textTight'}
                  data-testid={testId ? `${testId}-heading` : null}
                >
                  {heading}
                </h1>
                <div
                  className='u-marginTop20'
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(textContent ?? '') }}
                  data-testid={testId ? `${testId}-body` : null}
                />
                {buttonText && (
                  <a
                    className='Button Button--primary u-marginTop20 u-sm-block u-marginBottom32'
                    target={buttonTarget}
                    href={buttonUrl}
                    aria-label={buttonLinkAltText || ''}
                    data-analytics={analytics}
                    data-testid={testId ? `${testId}-link` : null}
                  >
                    {buttonText}
                  </a>
                )}
              </div>
            </div>
            <div
              className={
                'ComplexHero-topWrapper u-flex u-flexJustifyCenter u-lg-size6of12 u-md-marginLeft20 u-sm-marginLeft20'
              }
            >
              {featuredImage && (
                <>
                  {featuredImageCaption && (
                    <div
                      className='u-flex u-flexCol u-flexJustifyEnd u-flexAlignItemsRight u-paddingBottom32 u-sm-paddingBottom48 u-md-paddingBottom48 u-sm-paddingLeft0 u-md-paddingLeft24'
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(featuredImageCaption),
                      }}
                    ></div>
                  )}
                  <div
                    className={`ComplexHero-featuredImage${
                      noClip ? ' ComplexHero-featuredImage--noClip' : ''
                    }`}
                    style={{ backgroundImage: `url(${featuredImage})` }}
                    data-testid={testId ? `${testId}-featured-image` : null}
                  ></div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
